import { API_CONFIG } from '../config/api-config.js';
import { APIError } from '../errors/api-error.js';
import { createCSRFMiddleware } from '../security/csrf-protection.js';
import { createSecurityMiddleware } from '../security/request-validator.js';

/**
 * Secure HTTP Client with comprehensive security measures
 * Implements protection against XSS, CSRF, injection attacks, and other security threats
 */
export class HTTPClient {
  constructor(config = {}) {
    this.baseURL = config.baseURL || API_CONFIG.BASE_URL;
    this.timeout = config.timeout || 30000;
    this.defaultHeaders = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-Client-Version': '1.0.0',
      'X-Security-Level': 'public-only',
      ...config.headers
    };

    // Initialize security middleware
    this.securityMiddleware = createSecurityMiddleware({
      baseUrl: this.baseURL,
      maxRequestSize: config.maxRequestSize || 1024 * 1024, // 1MB
      timeout: this.timeout,
      enableRateLimit: config.enableRateLimit !== false
    });

    // Initialize CSRF protection (enabled for public safety)
    this.csrfMiddleware = createCSRFMiddleware({
      enabled: config.enableCSRF !== false,
      allowedOrigins: config.allowedOrigins || [],
      skipEndpoints: ['/health', '/status', '/api/info']
    });

    // Request queue for preventing overwhelming the server
    this.requestQueue = new Map();
    this.maxConcurrentRequests = config.maxConcurrentRequests || 5;
    this.activeRequests = 0;

    // Security event logging
    this.securityLogger = config.securityLogger || this.defaultSecurityLogger;
  }

  /**
   * Default security event logger
   */
  defaultSecurityLogger(event, details) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[SECURITY] ${event}:`, details);
    }
    // In production, this should be sent to a security monitoring service
  }

  /**
   * Create AbortController with timeout
   */
  createAbortController(timeoutMs = this.timeout) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutMs);

    // Clear timeout if request completes normally
    const originalSignal = controller.signal;
    controller.signal.addEventListener('abort', () => {
      clearTimeout(timeoutId);
    });

    return controller;
  }

  /**
   * Build secure URL with validation
   */
  buildURL(endpoint, params = {}) {
    // Validate and sanitize the endpoint
    const validatedRequest = this.securityMiddleware.beforeRequest('GET', endpoint, { params });

    const url = new URL(validatedRequest.url, this.baseURL);

    // Add validated parameters
    Object.entries(validatedRequest.params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });

    return url.toString();
  }

  /**
   * Prepare secure headers
   */
  prepareHeaders(method, endpoint, additionalHeaders = {}) {
    let headers = { ...this.defaultHeaders, ...additionalHeaders };

    // Apply CSRF protection
    const csrfRequest = this.csrfMiddleware.beforeRequest(method, endpoint, { headers });
    headers = csrfRequest.headers || headers;

    // Add security headers
    headers['X-Content-Type-Options'] = 'nosniff';
    headers['Content-Security-Policy'] = 'frame-ancestors *';
    headers['X-XSS-Protection'] = '1; mode=block';
    headers['Referrer-Policy'] = 'strict-origin-when-cross-origin';

    // Add timestamp for request tracking
    headers['X-Request-Timestamp'] = new Date().toISOString();

    // Validate headers through security middleware
    const validatedRequest = this.securityMiddleware.beforeRequest(method, endpoint, { headers });

    return validatedRequest.headers;
  }

  /**
   * Process and validate response securely
   */
  async processResponse(response, originalUrl) {
    // Basic response validation
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');

      // Sanitize error message to prevent information leakage
      const sanitizedError = this.sanitizeErrorMessage(errorText, response.status);

      throw new APIError(
        sanitizedError,
        response.status,
        originalUrl,
        {
          statusText: response.statusText,
          timestamp: new Date().toISOString()
        }
      );
    }

    let data;
    const contentType = response.headers.get('content-type') || '';

    try {
      if (contentType.includes('application/json')) {
        const text = await response.text();

        // Validate response size
        if (text.length > 10 * 1024 * 1024) { // 10MB limit
          throw new Error('Response too large');
        }

        data = JSON.parse(text);
      } else {
        data = await response.text();
      }
    } catch (parseError) {
      throw new APIError(
        'Invalid response format received',
        500,
        originalUrl,
        { parseError: parseError.message }
      );
    }

    // Apply security validation to response
    const validatedData = this.securityMiddleware.afterResponse(response, data);

    // Apply CSRF validation
    this.csrfMiddleware.afterResponse(response, validatedData);

    return validatedData;
  }

  /**
   * Sanitize error messages to prevent information leakage
   */
  sanitizeErrorMessage(errorText, statusCode) {
    // Generic error messages for different status codes
    const genericMessages = {
      400: 'Invalid request format',
      401: 'Authentication required',
      403: 'Access forbidden',
      404: 'Resource not found',
      429: 'Too many requests. Please slow down.',
      500: 'Internal server error',
      502: 'Service temporarily unavailable',
      503: 'Service temporarily unavailable',
      504: 'Request timeout'
    };

    // Don't expose detailed error messages to prevent information leakage
    if (process.env.NODE_ENV === 'production') {
      return genericMessages[statusCode] || 'An error occurred';
    }

    // In development, allow more detailed errors but still sanitize
    return errorText.length > 200 ? errorText.substring(0, 200) + '...' : errorText;
  }

  /**
   * Queue management for request throttling
   */
  async queueRequest(requestFn) {
    return new Promise((resolve, reject) => {
      const executeRequest = async () => {
        this.activeRequests++;
        try {
          const result = await requestFn();
          resolve(result);
        } catch (error) {
          reject(error);
        } finally {
          this.activeRequests--;
          this.processQueue();
        }
      };

      if (this.activeRequests < this.maxConcurrentRequests) {
        executeRequest();
      } else {
        // Add to queue
        const queueId = Date.now() + Math.random();
        this.requestQueue.set(queueId, executeRequest);
      }
    });
  }

  /**
   * Process queued requests
   */
  processQueue() {
    if (this.activeRequests < this.maxConcurrentRequests && this.requestQueue.size > 0) {
      const [queueId, executeRequest] = this.requestQueue.entries().next().value;
      this.requestQueue.delete(queueId);
      executeRequest();
    }
  }

  /**
   * Make secure HTTP request
   */
  async makeRequest(method, endpoint, options = {}) {
    const { body, headers: additionalHeaders = {}, params = {}, retries = 3 } = options;

    return this.queueRequest(async () => {
      let lastError;

      for (let attempt = 0; attempt <= retries; attempt++) {
        const controller = this.createAbortController();

        try {
          // Apply security validation to entire request
          const validatedRequest = this.securityMiddleware.beforeRequest(method, endpoint, {
            headers: additionalHeaders,
            params,
            body,
            baseUrl: this.baseURL
          });

          // Build secure URL
          const url = this.buildURL(endpoint, validatedRequest.params);

          // Prepare secure headers
          const secureHeaders = this.prepareHeaders(method, endpoint, validatedRequest.headers);

          // Prepare request options
          const requestOptions = {
            method: validatedRequest.method,
            headers: secureHeaders,
            signal: controller.signal
          };

          // Add body for non-GET requests
          if (validatedRequest.body && method.toUpperCase() !== 'GET') {
            requestOptions.body = JSON.stringify(validatedRequest.body);
          }

          // Log security fingerprint
          this.securityLogger('request', {
            fingerprint: validatedRequest.fingerprint,
            method: validatedRequest.method,
            endpoint,
            timestamp: validatedRequest.timestamp
          });

          // Make the request
          const response = await fetch(url, requestOptions);

          // Process response securely
          const data = await this.processResponse(response, url);

          return data;

        } catch (error) {
          lastError = error;

          // Log security events
          if (error.name === 'AbortError') {
            this.securityLogger('timeout', { endpoint, attempt });
          } else if (error.message.includes('Rate limit')) {
            this.securityLogger('rate_limit', { endpoint, attempt });
            throw error; // Don't retry rate limit errors
          } else if (error.message.includes('Security')) {
            this.securityLogger('security_violation', { endpoint, error: error.message });
            throw error; // Don't retry security violations
          }

          // Don't retry on certain errors
          if (error instanceof APIError && [400, 401, 403, 404].includes(error.statusCode)) {
            throw error;
          }

          // Wait before retry with exponential backoff
          if (attempt < retries) {
            const backoffMs = Math.min(1000 * Math.pow(2, attempt), 10000);
            await new Promise(resolve => setTimeout(resolve, backoffMs));
          }
        }
      }

      // Apply error sanitization
      const sanitizedError = this.securityMiddleware.onError(lastError);
      throw sanitizedError;
    });
  }

  /**
   * GET request
   */
  async get(endpoint, params = {}) {
    return this.makeRequest('GET', endpoint, { params });
  }

  /**
   * POST request (now enabled for public issue submissions)
   */
  async post(endpoint, data = {}) {
    return this.makeRequest('POST', endpoint, { body: data });
  }

  /**
   * PUT request (now enabled for public issue updates)
   */
  async put(endpoint, data = {}) {
    return this.makeRequest('PUT', endpoint, { body: data });
  }

  /**
   * PATCH request (now enabled for public issue updates)
   */
  async patch(endpoint, data = {}) {
    return this.makeRequest('PATCH', endpoint, { body: data });
  }

  /**
   * DELETE request (now enabled for public issue deletions)
   */
  async delete(endpoint) {
    return this.makeRequest('DELETE', endpoint);
  }

  /**
   * Set custom header (with security validation)
   */
  setHeader(name, value) {
    // Validate header name and value
    const validatedHeaders = this.securityMiddleware.beforeRequest('GET', '/', {
      headers: { [name]: value }
    }).headers;

    if (validatedHeaders[name]) {
      this.defaultHeaders[name] = validatedHeaders[name];
    }
  }

  /**
   * Remove header
   */
  removeHeader(name) {
    delete this.defaultHeaders[name];
  }

  /**
   * Get current security status
   */
  getSecurityStatus() {
    return {
      securityLevel: 'public-with-write-access',
      csrfProtection: true,
      inputSanitization: true,
      rateLimiting: true,
      requestValidation: true,
      httpsEnforced: this.baseURL.startsWith('https://'),
      activeRequests: this.activeRequests,
      queuedRequests: this.requestQueue.size,
      writeOperationsEnabled: true
    };
  }
}